import { Injectable, inject, Renderer2, RendererFactory2 } from '@angular/core';
import { ConfigService } from './config.service';
import { DOCUMENT } from '@angular/common';
import { CookieService } from './cookie.service';

declare global {
  interface Window {
    dataLayer: any[];
  }
}

export interface IGtm {
    category: string;
    action: string;
    label: string;
    value?: string;
}

export interface IGtmWorkflow {
    category: string;
    action: string;
    label: string;
    subjectId: string;
    workflowDescription: string;
    workflowStep: string;
    workflowTotalStep: string;
}

export enum Dimensions {
    dimension1 = 'CT Dimension 1',
    dimension2 = 'CT Dimension 2',
    dimension3 = 'CT Dimension 3',
    dimension4 = 'CT Dimension 4',
    dimension5 = 'CT Dimension 5',
    dimension6 = 'CT Dimension 6',
    dimension7 = 'CT Dimension 7',
    dimension8 = 'CT Dimension 8',
    dimension9 = 'CT Dimension 9',
    dimension10 = 'CT Dimension 10',
    dimension11 = 'CT Dimension 11',
    dimension12 = 'CT Dimension 12',
    dimension13 = 'CT Dimension 13',
    dimension14 = 'CT Dimension 14',
    dimension15 = 'CT Dimension 15',
    dimension16 = 'CT Dimension 16',
    dimension17 = 'CT Dimension 17',
    dimension18 = 'CT Dimension 18',
    dimension19 = 'CT Dimension 19',
    dimension20 = 'CT Dimension 20',
    dimension21 = 'CT Dimension 21',
    dimension22 = 'CT Dimension 22',
    dimension23 = 'CT Dimension 23',
    dimension24 = 'CT Dimension 24',
    dimension25 = 'CT Dimension 25',
    dimension26 = 'CT Dimension 26',
    dimension27 = 'CT Dimension 27',
    dimension28 = 'CT Dimension 28',
    dimension29 = 'CT Dimension 29',
    dimension30 = 'CT Dimension 30'
}

@Injectable({ providedIn: 'root' })
export class GtmService {
  private configService = inject(ConfigService);
  private cookieService = inject(CookieService);
  private rendererFactory = inject(RendererFactory2);
  private document = inject(DOCUMENT);
  private renderer: Renderer2;
  private isInnerNetwork = this.configService.getValue<boolean>('isInnerNetwork');

  constructor() {
    this.renderer = this.rendererFactory.createRenderer(null, null);

    if (!window.dataLayer) {
      window.dataLayer = [];
    }
  }

  public initializeGTM(): void {
    const config = this.configService.getConfig();

    if (!config) {
      console.warn('Config not loaded, cannot initialize GTM');
      return;
    }

    // Check if isInnerNetwork is false
    if (config.isInnerNetwork) {
      console.log('GTM not loaded: inner network detected');
      return;
    }

    // Check if noscript query param exists
    const urlParams = new URLSearchParams(this.document.location.search);
    if (urlParams.has('noscript')) {
      console.log('GTM not loaded: noscript parameter detected');
      return;
    }

    // Determine which GTM key to use based on webview query param
    const isWebView = urlParams.get('webview') === 'true';
    const gtmId = isWebView ? config.gtmKeyWebView : config.gtmKey;

    if (!gtmId) {
      console.warn('GTM ID not found in config');
      return;
    }

    // Inject GTM script
    this.injectGTMScript(gtmId);

    window.dataLayer.push({ 'aspSessionId': this.cookieService.getSessionId() });
  }

  private injectGTMScript(gtmId: string): void {
    const script = this.renderer.createElement('script');
    const scriptContent = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${gtmId}');
    `;

    const textNode = this.renderer.createText(scriptContent);
    this.renderer.appendChild(script, textNode);
    this.renderer.appendChild(this.document.head, script);

    console.log("GTM initialized");
  }

  push(category: string, action: string, label: string, value?: string) {
      this.isInnerNetwork
          ? console.log({ category, action, label, value })
          : this.pushToDataLayer({ category, action, label, value });
  }

  pushWorkflowGtm(
        category: string,
        action: string,
        label: string,
        subjectId: string,
        workflowDescription: string,
        workflowStep: string,
        workflowTotalStep: string
    ) {
        this.isInnerNetwork
            ? console.log({ category, action, label, subjectId, workflowDescription, workflowStep, workflowTotalStep })
            : this.pushToDataLayerWorkflow({
                  category,
                  action,
                  label,
                  subjectId,
                  workflowDescription,
                  workflowStep,
                  workflowTotalStep
              });
    }

  private pushToDataLayer(gtm: IGtm): void {
        if (!window.dataLayer) {
            window.dataLayer = [];
        }

        window.dataLayer.push({
            Category: gtm.category,
            Action: gtm.action,
            Label: gtm.label,
            SubjectId: undefined,
            WorkflowDescription: undefined,
            WorkflowStep: undefined,
            WorkflowTotalStep: undefined,
            event: 'auto_event',
            Value: gtm.value
        });
    }

    private pushToDataLayerWorkflow(gtm: IGtmWorkflow): void {
        if (!window.dataLayer) {
            window.dataLayer = [];
        }

        window.dataLayer.push({
            Category: gtm.category,
            Action: gtm.action,
            Label: gtm.label,
            SubjectId: gtm.subjectId,
            WorkflowDescription: gtm.workflowDescription,
            WorkflowStep: gtm.workflowStep,
            WorkflowTotalStep: gtm.workflowTotalStep,
            event: 'auto_event'
        });
    }
}
